import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowDown, ArrowUp, ExternalLink, RefreshCcw } from 'lucide-react';
import { AuthContext } from '../../utils/auth.js';

const PAGE_SIZE = 25;

function createdTime(value) {
    if (!value) return null;
    const date = new Date(typeof value === 'number' ? value : Number(value) || value);
    const time = date.getTime();
    return Number.isNaN(time) ? null : time;
}

function formatCreatedAt(value) {
    const time = createdTime(value);
    if (time == null) return '—';
    return new Date(time).toLocaleString();
}

const EXCLUDED_EMAILS_KEY = 'adminWorkflowExtractionsExcludedEmails';

function includesText(value, query) {
    if (!query) return true;
    return String(value || '').toLowerCase().includes(query);
}

function readExcludedEmails() {
    try {
        return window.localStorage.getItem(EXCLUDED_EMAILS_KEY) || '';
    } catch (error) {
        return '';
    }
}

function parseExcludedEmails(value) {
    return String(value || '')
        .split(/[\s,;]+/)
        .map((part) => part.trim().toLowerCase())
        .filter(Boolean);
}

function splitActiveEmail(value) {
    const raw = String(value || '');
    const separator = Math.max(raw.lastIndexOf(','), raw.lastIndexOf(';'));
    if (separator === -1) return { head: '', token: raw.trim() };
    return {
        head: raw.slice(0, separator + 1),
        token: raw.slice(separator + 1).trim(),
    };
}

function EmailSuggestInput({
    id,
    label,
    value,
    onChange,
    emails,
    placeholder,
    multi = false,
    className = '',
}) {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const boxRef = useRef(null);
    const token = multi ? splitActiveEmail(value).token : String(value || '').trim();
    const query = token.toLowerCase();

    const suggestions = useMemo(() => {
        if (!query) return [];
        const alreadySelected = multi ? new Set(parseExcludedEmails(value)) : new Set();
        return emails.filter((email) => {
            const lower = email.toLowerCase();
            if (alreadySelected.has(lower)) return false;
            return lower.includes(query);
        }).slice(0, 8);
    }, [emails, query, multi, value]);

    useEffect(() => {
        setHighlight(0);
    }, [query]);

    useEffect(() => {
        const onPointerDown = (event) => {
            if (!boxRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, []);

    const applyEmail = (email) => {
        if (multi) {
            const { head } = splitActiveEmail(value);
            const next = head
                ? `${head.trim().replace(/[,;]\s*$/, '')}, ${email}, `
                : `${email}, `;
            onChange(next);
        } else {
            onChange(email);
        }
        setOpen(false);
    };

    const onKeyDown = (event) => {
        if (!open || suggestions.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlight((index) => (index + 1) % suggestions.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlight((index) => (index - 1 + suggestions.length) % suggestions.length);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            applyEmail(suggestions[highlight] || suggestions[0]);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div className={className} ref={boxRef}>
            <label htmlFor={id} className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                {label}
            </label>
            <div className="relative">
                <input
                    id={id}
                    type="text"
                    value={value}
                    autoComplete="off"
                    onChange={(event) => {
                        onChange(event.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                {open && suggestions.length > 0 && (
                    <ul className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
                        {suggestions.map((email, index) => (
                            <li key={email}>
                                <button
                                    type="button"
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        applyEmail(email);
                                    }}
                                    onMouseEnter={() => setHighlight(index)}
                                    className={`block w-full px-3 py-2 text-left text-sm ${
                                        index === highlight ? 'bg-indigo-50 text-indigo-800' : 'text-slate-700'
                                    }`}
                                >
                                    {email}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default function WorkflowExtractionsAdmin({ users = {} }) {
    const { currentUser } = useContext(AuthContext);
    const serverUrl = process.env.REACT_APP_SERVER_URL;

    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRebuilding, setIsRebuilding] = useState(false);
    const [error, setError] = useState('');

    const [extractionId, setExtractionId] = useState('');
    const [userId, setUserId] = useState('');
    const [workflowId, setWorkflowId] = useState('');
    const [excludedEmails, setExcludedEmails] = useState(readExcludedEmails);
    const [sortKey, setSortKey] = useState('created');
    const [sortOrder, setSortOrder] = useState('desc');
    const [debouncedFilters, setDebouncedFilters] = useState({
        extractionId: '',
        userId: '',
        workflowId: '',
        excludedEmails: parseExcludedEmails(readExcludedEmails()),
    });
    const [page, setPage] = useState(1);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilters({
                extractionId: extractionId.trim().toLowerCase(),
                userId: userId.trim().toLowerCase(),
                workflowId: workflowId.trim().toLowerCase(),
                excludedEmails: parseExcludedEmails(excludedEmails),
            });
        }, 250);
        return () => clearTimeout(timer);
    }, [extractionId, userId, workflowId, excludedEmails]);

    useEffect(() => {
        try {
            window.localStorage.setItem(EXCLUDED_EMAILS_KEY, excludedEmails);
        } catch (error) {
            // The list still filters for this session if storage is blocked.
        }
    }, [excludedEmails]);

    const applyPayload = (data) => {
        setRows(Array.isArray(data?.extractions) ? data.extractions : []);
        setMeta({
            scannedUsers: data?.scannedUsers || data?.rebuild?.scannedUsers || 0,
            totalUsers: data?.totalUsers || data?.rebuild?.totalUsers || 0,
            failedUsers: data?.failedUsers || data?.rebuild?.failedUsers || 0,
            matched: data?.matched || 0,
            indexedAt: data?.indexedAt || null,
            rebuild: data?.rebuild || null,
        });
    };

    const fetchExtractions = useCallback(async ({ silent = false } = {}) => {
        if (!currentUser) return;
        if (!silent) setIsLoading(true);
        setError('');
        try {
            const idToken = await currentUser.getIdToken();
            const response = await axios.get(`${serverUrl}/admin/workflowExtractions`, {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
                timeout: 60000,
            });
            applyPayload(response.data);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load workflow extractions');
        } finally {
            if (!silent) setIsLoading(false);
        }
    }, [currentUser, serverUrl]);

    const startRebuild = useCallback(async () => {
        if (!currentUser) return;
        setIsRebuilding(true);
        setError('');
        try {
            const idToken = await currentUser.getIdToken();
            await axios.post(`${serverUrl}/admin/workflowExtractions/rebuild`, {}, {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
                timeout: 30000,
            });
            await fetchExtractions({ silent: true });
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to start the scan');
        } finally {
            setIsRebuilding(false);
        }
    }, [currentUser, serverUrl, fetchExtractions]);

    useEffect(() => {
        fetchExtractions();
    }, [fetchExtractions]);

    const rebuildStatus = meta?.rebuild?.status;
    useEffect(() => {
        if (rebuildStatus !== 'running') return undefined;
        const timer = setInterval(() => {
            fetchExtractions({ silent: true });
        }, 3000);
        return () => clearInterval(timer);
    }, [rebuildStatus, fetchExtractions]);

    const enrichedRows = useMemo(() => {
        return rows.map((row) => ({
            ...row,
            email: users[row.userId] || '',
        }));
    }, [rows, users]);

    const workflowOptions = useMemo(() => {
        return Array.from(new Set(enrichedRows.map((row) => row.workflowId).filter(Boolean))).sort();
    }, [enrichedRows]);

    const availableEmails = useMemo(() => {
        return Array.from(new Set(enrichedRows.map((row) => row.email).filter(Boolean)))
            .sort((a, b) => a.localeCompare(b));
    }, [enrichedRows]);

    const filteredRows = useMemo(() => {
        const excluded = debouncedFilters.excludedEmails || [];
        return enrichedRows.filter((row) => {
            const email = String(row.email || '').toLowerCase();
            if (excluded.some((term) => email.includes(term))) return false;
            if (!includesText(row.extractionId, debouncedFilters.extractionId)) return false;
            if (!includesText(row.workflowId, debouncedFilters.workflowId)) return false;
            if (!debouncedFilters.userId) return true;
            return includesText(row.userId, debouncedFilters.userId)
                || includesText(row.email, debouncedFilters.userId);
        });
    }, [enrichedRows, debouncedFilters]);

    const hiddenByEmail = useMemo(() => {
        const excluded = debouncedFilters.excludedEmails || [];
        if (!excluded.length) return 0;
        return enrichedRows.filter((row) => {
            const email = String(row.email || '').toLowerCase();
            return excluded.some((term) => email.includes(term));
        }).length;
    }, [enrichedRows, debouncedFilters]);

    const userCount = useMemo(() => new Set(filteredRows.map((row) => row.userId)).size, [filteredRows]);
    const workflowCount = useMemo(() => new Set(filteredRows.map((row) => row.workflowId)).size, [filteredRows]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortOrder((order) => (order === 'desc' ? 'asc' : 'desc'));
            return;
        }
        setSortKey(key);
        setSortOrder('desc');
    };

    const sortedRows = useMemo(() => {
        const direction = sortOrder === 'asc' ? 1 : -1;
        return [...filteredRows].sort((a, b) => {
            const aTime = createdTime(sortKey === 'lastUpload' ? a.lastUploadAt : a.createdAt);
            const bTime = createdTime(sortKey === 'lastUpload' ? b.lastUploadAt : b.createdAt);
            if (aTime == null && bTime == null) return 0;
            if (aTime == null) return 1;
            if (bTime == null) return -1;
            return (aTime - bTime) * direction;
        });
    }, [filteredRows, sortKey, sortOrder]);

    const scanning = rebuildStatus === 'running' || isRebuilding;
    const pageCount = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount);
    const pageRows = sortedRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    useEffect(() => {
        setPage(1);
    }, [debouncedFilters, sortKey, sortOrder]);

    return (
        <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="text-2xl font-bold text-slate-800">{filteredRows.length.toLocaleString()}</div>
                    <div className="mt-1 text-sm text-slate-500">Extractions</div>
                </div>
                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="text-2xl font-bold text-slate-800">{userCount.toLocaleString()}</div>
                    <div className="mt-1 text-sm text-slate-500">Users</div>
                </div>
                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="text-2xl font-bold text-slate-800">{workflowCount.toLocaleString()}</div>
                    <div className="mt-1 text-sm text-slate-500">Workflows</div>
                </div>
                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="text-2xl font-bold text-slate-800">
                        {scanning
                            ? `${(meta?.scannedUsers || 0).toLocaleString()}/${(meta?.totalUsers || 0).toLocaleString()}`
                            : (meta?.totalUsers || 0).toLocaleString()}
                    </div>
                    <div className="mt-1 text-sm text-slate-500">{scanning ? 'Users scanned' : 'Users in last scan'}</div>
                </div>
            </div>

            <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                    <label className="block text-sm">
                        <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Extraction ID</span>
                        <input
                            type="text"
                            value={extractionId}
                            onChange={(event) => setExtractionId(event.target.value)}
                            placeholder="Filter by extraction id"
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                    </label>
                    <EmailSuggestInput
                        id="workflow-user-search"
                        label="User ID"
                        value={userId}
                        onChange={setUserId}
                        emails={availableEmails}
                        placeholder="Filter by user id or email"
                    />
                    <label className="block text-sm">
                        <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Workflow ID</span>
                        <input
                            type="text"
                            value={workflowId}
                            onChange={(event) => setWorkflowId(event.target.value)}
                            placeholder="Filter by workflow id"
                            list="workflow-id-options"
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                        <datalist id="workflow-id-options">
                            {workflowOptions.map((option) => (
                                <option key={option} value={option} />
                            ))}
                        </datalist>
                    </label>
                    <EmailSuggestInput
                        id="workflow-exclude-emails"
                        className="lg:col-span-3"
                        label="Exclude emails"
                        value={excludedEmails}
                        onChange={setExcludedEmails}
                        emails={availableEmails}
                        placeholder="Hide these users. Separate emails with commas."
                        multi
                    />
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-slate-500">
                        {filteredRows.length.toLocaleString()} of {enrichedRows.length.toLocaleString()} workflow extractions
                        {hiddenByEmail > 0 ? ` · ${hiddenByEmail.toLocaleString()} hidden by email` : ''}
                        {scanning
                            ? ` · scanning ${(meta?.scannedUsers || 0).toLocaleString()} of ${(meta?.totalUsers || 0).toLocaleString()} users`
                            : ''}
                        {!scanning && meta?.indexedAt ? ` · updated ${new Date(meta.indexedAt).toLocaleString()}` : ''}
                    </p>
                    <button
                        type="button"
                        onClick={startRebuild}
                        disabled={isLoading || scanning}
                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${
                            isLoading || scanning
                                ? 'cursor-not-allowed bg-slate-200 text-slate-400'
                                : 'bg-indigo-600 text-white hover:bg-indigo-500'
                        }`}
                    >
                        <RefreshCcw className={`h-4 w-4 ${scanning ? 'animate-spin' : ''}`} />
                        {scanning ? 'Scanning...' : 'Rescan'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
                    {error}
                </div>
            )}

            {meta?.failedUsers > 0 && !scanning && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    {meta.failedUsers} user {meta.failedUsers === 1 ? 'folder' : 'folders'} could not be read. Rescan to try again.
                </div>
            )}

            {(isLoading || scanning) && rows.length === 0 && (
                <div className="rounded-xl border border-slate-100 bg-white p-8 text-center shadow-sm">
                    <RefreshCcw className="mx-auto h-6 w-6 animate-spin text-indigo-600" />
                    <p className="mt-3 text-sm font-medium text-slate-700">
                        {scanning ? 'Scanning extractions for a workflow id' : 'Loading workflow extractions'}
                    </p>
                    {scanning && (
                        <p className="mt-1 text-sm text-slate-500">
                            This walks every user once, then the list stays available. Matches show up as they are found.
                        </p>
                    )}
                </div>
            )}

            {!isLoading && !scanning && !error && filteredRows.length === 0 && (
                <div className="rounded-xl border border-slate-100 bg-white p-10 text-center shadow-sm">
                    <h3 className="text-lg font-medium text-slate-900">No workflow extractions</h3>
                    <p className="mt-2 text-sm text-slate-500">
                        {enrichedRows.length === 0
                            ? (meta?.indexedAt
                                ? 'No extractions with a workflow id were found.'
                                : 'The list has not been built yet. Use Rescan to look through every user.')
                            : 'Nothing matches these filters.'}
                    </p>
                </div>
            )}

            {pageRows.length > 0 && (
                <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Extraction</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">User</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Workflow ID</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        <button
                                            type="button"
                                            onClick={() => toggleSort('created')}
                                            className="inline-flex items-center gap-1 uppercase tracking-wide text-slate-500 hover:text-slate-800"
                                        >
                                            Created
                                            {sortKey === 'created' && (sortOrder === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />)}
                                        </button>
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        <button
                                            type="button"
                                            onClick={() => toggleSort('lastUpload')}
                                            className="inline-flex items-center gap-1 uppercase tracking-wide text-slate-500 hover:text-slate-800"
                                        >
                                            Last upload
                                            {sortKey === 'lastUpload' && (sortOrder === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />)}
                                        </button>
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {pageRows.map((row) => {
                                    const href = `/extractions/${row.extractionId}?adminUserId=${encodeURIComponent(row.userId)}`;
                                    return (
                                        <tr key={`${row.userId}-${row.extractionId}`} className="hover:bg-slate-50">
                                            <td className="px-3 py-3 align-top">
                                                <a
                                                    href={href}
                                                    className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-500"
                                                >
                                                    {row.name || 'Untitled extraction'}
                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                </a>
                                                <div className="mt-1 font-mono text-xs text-slate-500">{row.extractionId}</div>
                                            </td>
                                            <td className="px-3 py-3 align-top">
                                                <div className="text-sm text-slate-800">{row.email || '—'}</div>
                                                <div className="mt-1 font-mono text-xs text-slate-500">{row.userId}</div>
                                            </td>
                                            <td className="px-3 py-3 align-top">
                                                <span className="inline-flex rounded-md bg-indigo-50 px-2 py-1 font-mono text-xs font-medium text-indigo-800">
                                                    {row.workflowId}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 align-top text-sm text-slate-700">{formatCreatedAt(row.createdAt)}</td>
                                            <td className="px-3 py-3 align-top text-sm text-slate-700">
                                                {row.lastUploadAt ? formatCreatedAt(row.lastUploadAt) : 'No uploads'}
                                            </td>
                                            <td className="px-3 py-3 align-top text-sm text-slate-700">
                                                <div>{row.status || '—'}</div>
                                                {row.language ? <div className="mt-1 text-xs text-slate-400">{row.language}</div> : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {pageCount > 1 && (
                        <div className="flex items-center justify-between border-t border-slate-100 px-3 py-3">
                            <p className="text-sm text-slate-500">
                                Page {currentPage} of {pageCount}
                            </p>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                                    disabled={currentPage === 1}
                                    className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:text-slate-300"
                                >
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
                                    disabled={currentPage === pageCount}
                                    className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:text-slate-300"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
